import React, { useState, useRef, useEffect } from 'react';
import { User, Mail, Phone, Building2, Shield, Key, Save, Camera, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';
import axios from 'axios';

const Profile = () => {
  const { user, updateProfile, changePassword, refreshUser } = useAuth();
  const { t } = useApp();
  const fileInputRef = useRef(null);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Update profileData when user loads
  useEffect(() => {
    if (user) {
      console.log('[Profile] User loaded:', user);
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrator',
      kepala_cabang: 'Kepala Cabang',
      cs: 'Customer Service',
      teknisi: 'Teknisi'
    };
    return roles[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-500',
      kepala_cabang: 'bg-blue-500',
      cs: 'bg-emerald-500',
      teknisi: 'bg-amber-500'
    };
    return colors[role] || 'bg-slate-500';
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const result = await updateProfile(profileData);
    
    if (result.success) {
      toast.success('Profile updated successfully');
    } else {
      toast.error(result.error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    const result = await changePassword(passwordData.old_password, passwordData.new_password);
    
    if (result.success) {
      toast.success('Password changed successfully');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } else {
      toast.error(result.error);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format file harus JPG, PNG, atau WebP');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setAvatarFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('avatar', avatarFile);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${API_URL}/api/users/upload-avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast.success('Foto profile berhasil diupload!');
        setAvatarPreview(null);
        setAvatarFile(null);
        // Refresh user data without page reload
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal upload foto profile');
    } finally {
      setUploading(false);
    }
  };

  const cancelAvatarUpload = () => {
    setAvatarPreview(null);
    setAvatarFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 dark:text-slate-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Profile Overview */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-6">
            {/* Avatar with Upload Button */}
            <div className="relative group">
              <div className="w-28 h-28 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-4xl font-bold backdrop-blur-sm overflow-hidden border-4 border-white/30 dark:bg-slate-800">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : user.avatar_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${user.avatar_url}?t=${Date.now()}`} alt={user.full_name || 'User'} className="w-full h-full object-cover" onError={(e) => { console.error('Avatar load failed:', e.target.src); }} />
                ) : (
                  (user.full_name || user.email || 'U').charAt(0).toUpperCase()
                )}
              </div>
              {!avatarPreview ? (
                <button
                  onClick={handleAvatarClick}
                  className="absolute bottom-0 right-0 bg-white text-indigo-600 rounded-full p-2 shadow-lg hover:bg-indigo-50 transition-all transform hover:scale-110 dark:bg-slate-800 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                  title="Upload Foto Profile"
                >
                  <Camera className="w-4 h-4" />
                </button>
              ) : (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <button
                    onClick={handleUploadAvatar}
                    disabled={uploading}
                    className="bg-green-500 text-white rounded-full px-3 py-1 text-xs font-medium shadow-lg hover:bg-green-600 transition-all disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Save'}
                  </button>
                  <button
                    onClick={cancelAvatarUpload}
                    disabled={uploading}
                    className="bg-red-500 text-white rounded-full px-3 py-1 text-xs font-medium shadow-lg hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{user.full_name || user.email || 'User'}</h2>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge className={`${getRoleBadgeColor(user.role)} hover:${getRoleBadgeColor(user.role)} text-xs px-2 py-0.5`}>
                  <Shield className="w-3 h-3 mr-1" />
                  {getRoleLabel(user.role)}
                </Badge>
                <span className="text-sm opacity-80">@{user.username || user.email}</span>
              </div>
              <div className="mt-2 pt-2 border-t border-white/20">
                <p className="text-sm opacity-75">{user?.tenant_name || ""}</p>
                <p className="text-xs opacity-60">ISP Billing</p>
              </div>
              {avatarFile && (
                <div className="mt-3 p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                  <p className="text-xs">
                    📸 <strong>{avatarFile.name}</strong> ({(avatarFile.size / 1024).toFixed(0)} KB)
                  </p>
                  <p className="text-xs opacity-75 mt-1">
                    💡 Max 2MB, Format: JPG/PNG/WebP, Recommended: 400x400px
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Information */}
      <Card className="border-0 shadow-lg dark:bg-slate-800">
        <CardHeader className="border-b border-slate-200 dark:border-slate-700">
          <CardTitle className="flex items-center space-x-2 dark:text-white">
            <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <span>Profile Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-white">Full Name</Label>
                <Input
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="dark:text-white">Email</Label>
                <Input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="dark:text-white">Phone</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="dark:text-white">Username (Read-only)</Label>
                <Input
                  value={user.username}
                  disabled
                  className="bg-slate-100 dark:bg-slate-700 dark:text-slate-400"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="bg-gradient-to-r from-indigo-600 to-purple-600">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-0 shadow-lg dark:bg-slate-800">
        <CardHeader className="border-b border-slate-200 dark:border-slate-700">
          <CardTitle className="flex items-center space-x-2 dark:text-white">
            <Key className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <span>Change Password</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="dark:text-white">Current Password</Label>
              <Input
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                required
                className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-white">New Password</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  required
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label className="dark:text-white">Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  required
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700">
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;